// <purpose>The press as a host runs it (decide, then step; decision 2026-10-28) — the ONE flow the
// list page and the pop-out share, so a yes or a no behaves the same in both: the verdict shows at
// once (optimistic), the after-press card rises above the pressed slot, the server call follows,
// and a refusal rolls the ticket back with a message on the bar — never a spinner, never a blank
// bar. Rückgängig stands 30 s; when it runs out only Rückgängig fades, nothing navigates.</purpose>
// <dependencies>services/pressVerdict (the optimistic shapes), TicketManagerContext (the mutations,
// cross-window announce included). The LIST reaction — holding the decided row, its leave, the next
// row — is the list page's, through the callbacks; the pop-out passes none of them.</dependencies>
// <constraints>Two plain calls: a press decides; stepping on is a separate act the host offers
// (Nächste Mail). A press from a row that is already decided (the second WIM letter, from
// Entschieden) raises no card: core's undo reopens the whole mail, so there is no per-letter undo
// to offer.</constraints>
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DeclineInstead, DraftKind, TicketDetail, TicketDecline } from '../serverTypes';
import { useTicketManager } from '../context/TicketManagerContext';
import { isStaleSuggestionError } from '../services/staleSuggestion';
import { isSlotsOpenError } from '../services/draftClaims';
import {
  PRESS_UNDO_MS, applyDecline, applyKindApprove, applyLetterApprove, applyReopen, type PressVerdict,
} from '../services/pressVerdict';

export interface PressState {
  uuid: string;
  subject: string;
  verdict: PressVerdict;
  /** The kind the last approval in this press named (WIM); null for a letter-flow yes or a no. */
  lastKind: DraftKind | null;
  decline: TicketDecline | null;
  side: 'left' | 'right';
  mode: 'full' | 'line';
  undoUntil: number;
  /** The decided ticket as the press left it — the undo reopens from this when the mail has left. */
  snapshot: TicketDetail;
}

export interface UsePressOptions {
  /** Patch the ticket on screen when it is this uuid. */
  patchTicket: (uuid: string, fn: (t: TicketDetail) => TicketDetail) => void;
  /** A silent re-read of this ticket (only applied if it is still the one on screen). */
  refetchTicket: (uuid: string) => Promise<void>;
  onStale: (uuid: string) => void;
  /** The verdict stands on screen (the list holds the row); `decided` = the ticket as pressed. */
  onPressed?: (uuid: string, verdict: PressVerdict, decided: TicketDetail) => void;
  /** The server took it. */
  onConfirmed?: (uuid: string, verdict: PressVerdict) => void;
  /** The server refused; the ticket is already rolled back. */
  onRefused?: (uuid: string) => void;
  /** Rückgängig pressed: the reopened ticket to show (the list brings the row back, selected). */
  onUndo?: (uuid: string, reopened: TicketDetail) => void;
  /** The undo's answer: the server reopened the mail, or refused. */
  onUndoSettled?: (uuid: string, ok: boolean) => void;
  /** A letter approved on a mail already decided (the second WIM letter): the server took it. */
  onLaterLetter?: (uuid: string) => void;
}

const FAIL = {
  approve: 'Fehler beim Genehmigen',
  decline: 'Fehler beim Ablegen',
  undo: 'Fehler beim Rückgängigmachen',
  claim: 'Die Aktion konnte nicht ausgeführt werden.',
} as const;

export function usePress(opts: UsePressOptions) {
  const tm = useTicketManager();
  const [press, setPress] = useState<PressState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [, setTick] = useState(0);
  // The callbacks are read through a ref: hosts pass fresh closures every render.
  const o = useRef(opts);
  o.current = opts;
  const pressRef = useRef(press);
  pressRef.current = press;

  const undoLive = !!press && Date.now() < press.undoUntil;

  // The undo window: at its end Rückgängig fades (a re-render); a collapsed line then leaves too.
  useEffect(() => {
    if (!press) return undefined;
    const left = press.undoUntil - Date.now();
    if (left <= 0) {
      if (press.mode === 'line') {
        const t = setTimeout(() => setPress(p => (p === press ? null : p)), 200);
        return () => clearTimeout(t);
      }
      return undefined;
    }
    const t = setTimeout(() => setTick(n => n + 1), left + 10);
    return () => clearTimeout(t);
  }, [press]);

  const start = (ticket: TicketDetail, decided: TicketDetail, verdict: PressVerdict, lastKind: DraftKind | null) => {
    setMessage(null);
    o.current.patchTicket(ticket.uuid, () => decided);
    setPress({
      uuid: ticket.uuid, subject: ticket.subject ?? '', verdict, lastKind,
      decline: decided.decline ?? null, side: verdict === 'approved' ? 'left' : 'right',
      mode: 'full', undoUntil: Date.now() + PRESS_UNDO_MS, snapshot: decided,
    });
    o.current.onPressed?.(ticket.uuid, verdict, decided);
  };

  const settle = async (
    ticket: TicketDetail, verdict: PressVerdict,
    call: () => Promise<{ success: boolean; error?: string }>, fallback: string,
  ) => {
    const result = await call();
    const uuid = ticket.uuid;
    if (result.success) {
      o.current.onConfirmed?.(uuid, verdict);
      void o.current.refetchTicket(uuid);
      return;
    }
    o.current.patchTicket(uuid, () => ticket);
    setPress(p => (p && p.uuid === uuid ? null : p));
    o.current.onRefused?.(uuid);
    if (isStaleSuggestionError(result)) { o.current.onStale(uuid); return; }
    if (isSlotsOpenError(result)) void o.current.refetchTicket(uuid);
    setMessage(result.error || fallback);
  };

  /** Genehmigen on a letter-flow row. */
  const approve = useCallback(async (ticket: TicketDetail) => {
    if (ticket.status !== 'suggestion_ready') return;
    start(ticket, applyLetterApprove(ticket), 'approved', null);
    await settle(ticket, 'approved', () => tm.approveTicket(ticket.uuid), FAIL.approve);
  }, [tm]); // eslint-disable-line react-hooks/exhaustive-deps

  /** „Nicht hilfreich" with its reason (null = Ohne Angabe). */
  const decline = useCallback(async (ticket: TicketDetail, instead: DeclineInstead | null) => {
    if (ticket.status !== 'suggestion_ready') return;
    start(ticket, applyDecline(ticket, instead), 'declined', null);
    await settle(ticket, 'declined', () => tm.declineTicket(ticket.uuid, instead), FAIL.decline);
  }, [tm]); // eslint-disable-line react-hooks/exhaustive-deps

  /** One kind's approval (WIM). From an open row it is a press with its card; from a row already
   *  decided — the second letter — it is only the claim (no card), except while
   *  this mail's own card still stands (A): then the card names the newest letter. */
  const approveKind = useCallback(async (ticket: TicketDetail, kind: DraftKind) => {
    const suggestionId = ticket.suggestion?.id;
    if (suggestionId == null) return;
    const call = () => tm.claimDraft(ticket.uuid, suggestionId, kind, 'approve');
    if (ticket.status === 'suggestion_ready') {
      start(ticket, applyKindApprove(ticket, kind), 'approved', kind);
      await settle(ticket, 'approved', call, FAIL.claim);
      return;
    }
    setMessage(null);
    o.current.patchTicket(ticket.uuid, t => applyKindApprove(t, kind));
    const p = pressRef.current;
    if (p && p.uuid === ticket.uuid) setPress({ ...p, lastKind: kind, snapshot: applyKindApprove(p.snapshot, kind) });
    const result = await call();
    if (result.success) { o.current.onLaterLetter?.(ticket.uuid); void o.current.refetchTicket(ticket.uuid); return; }
    o.current.patchTicket(ticket.uuid, () => ticket);
    if (p && p.uuid === ticket.uuid) setPress(cur => (cur && cur.uuid === ticket.uuid ? { ...cur, lastKind: p.lastKind, snapshot: p.snapshot } : cur));
    if (isStaleSuggestionError(result)) { o.current.onStale(ticket.uuid); return; }
    if (isSlotsOpenError(result)) void o.current.refetchTicket(ticket.uuid);
    setMessage(result.error || FAIL.claim);
  }, [tm]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Rückgängig: the mail reopens at once; the server confirms or the re-read puts it back. */
  const undo = useCallback(async () => {
    const p = pressRef.current;
    if (!p || Date.now() >= p.undoUntil) return;
    setPress(null);
    setMessage(null);
    const reopened = applyReopen(p.snapshot);
    o.current.patchTicket(p.uuid, t => applyReopen(t));
    o.current.onUndo?.(p.uuid, reopened);
    const result = p.verdict === 'approved' ? await tm.withdrawApproval(p.uuid) : await tm.withdrawDecline(p.uuid);
    o.current.onUndoSettled?.(p.uuid, result.success);
    if (!result.success) {
      setMessage(result.error || FAIL.undo);
      void o.current.refetchTicket(p.uuid);
    }
  }, [tm]);

  /** Hier bleiben / moving on: the card collapses to its undo line. */
  const toLine = useCallback(() => setPress(p => (p && p.mode === 'full' ? { ...p, mode: 'line' } : p)), []);
  /** The press is over for this host (archived, re-analysed, a new mail decided elsewhere). */
  const clear = useCallback((uuid?: string) => setPress(p => (!p || (uuid && p.uuid !== uuid) ? p : null)), []);
  const dismissMessage = useCallback(() => setMessage(null), []);

  return { press, undoLive, message, dismissMessage, approve, decline, approveKind, undo, toLine, clear };
}
